/**
 * Profile Page
 * Main orchestrator for profile components
 */

import { Loader2 } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useProfileData } from './hooks/useProfileData';
import { useStorePreferences } from './hooks/useStorePreferences';
import {
  ProfileHero,
  LevelCard,
  StatsGrid,
  BadgesCard,
  EarnXpCard,
  LevelRewardsCard,
  StorePreferencesCard,
} from './components';

export default function Profile() {
  const { t } = useLanguage();
  const profileData = useProfileData();
  const storePrefs = useStorePreferences();

  // Loading state
  if (profileData.isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
          <p className="text-gray-600">{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <ProfileHero stats={profileData.heroStats} />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Level Card */}
        <div className="animate-fade-in">
          <LevelCard
            level={profileData.stats.level}
            currentLevelInfo={profileData.currentLevelInfo}
            nextLevelInfo={profileData.nextLevelInfo}
            xpProgress={profileData.xpProgress}
            getLevelIcon={profileData.getLevelIcon}
          />
        </div>

        {/* Stats Grid */}
        <div className="animate-fade-in animation-delay-100">
          <StatsGrid stats={profileData.stats} />
        </div>

        {/* Two Column Layout for larger screens */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-8">
            {/* Store Preferences */}
            <div className="animate-fade-in animation-delay-200">
              <StorePreferencesCard storePrefs={storePrefs} />
            </div>

            {/* Earn XP */}
            <div className="animate-fade-in animation-delay-300">
              <EarnXpCard />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Badges */}
            <div className="animate-fade-in animation-delay-200">
              <BadgesCard
                allBadges={profileData.allBadges}
                earnedCount={profileData.earnedCount}
                isBadgeEarned={profileData.isBadgeEarned}
                getBadgeEarnedDate={profileData.getBadgeEarnedDate}
              />
            </div>
          </div>
        </div>

        {/* Level Rewards - Full Width */}
        <div className="animate-fade-in animation-delay-400">
          <LevelRewardsCard currentLevel={profileData.stats.level} />
        </div>
      </div>
    </div>
  );
}
